/* Melora Archive — the visitor's country, so shipping copy can speak to where they are.

   Used by the announcement bar and the "Free shipping" badge on the product page. Any element with
   `data-geo-shipping-text` is rewritten from its `data-geo-shipping-template` ("... __COUNTRY__ ...")
   once the visitor's country is known. If it can't be known, or the store doesn't sell there, the
   merchant's own text is left alone.

   The country comes from a client-side IP lookup — not Shopify's `localization.country` (that only
   changes when a visitor picks a country in the selector) and not `navigator.geolocation` (which
   triggers a browser permission prompt). Two independent providers, so a rate limit or an outage on
   one doesn't take the feature down. The answer is cached for the session, shared by every consumer.

   Config (snippets/geo-country.liquid): `supported` is the list of country codes in Shopify Markets,
   so reducing the markets reduces the countries personalised here with no theme change;
   `names` is how a country reads after "to" where the plain name doesn't ("the UAE"). */
(() => {
  if (window.MeloraGeo) return;

  const CACHE_KEY = 'melora:geo-country';
  const CACHE_TTL = 12 * 60 * 60 * 1000;
  const SELECTOR = '[data-geo-shipping-text]';

  let config = { supported: null, names: {} };

  try {
    config = Object.assign(config, JSON.parse(document.getElementById('melora-geo-config').textContent));
  } catch (error) {
    /* no config: personalise every detected country, plain names */
  }

  const readCache = () => {
    try {
      const data = JSON.parse(sessionStorage.getItem(CACHE_KEY));
      return data && Date.now() - data.t <= CACHE_TTL ? data : null;
    } catch (error) {
      return null;
    }
  };

  const writeCache = (country, countryCode) => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ country, countryCode, t: Date.now() }));
    } catch (error) {
      /* private mode / storage disabled: we just look it up again next time */
    }
  };

  const fetchJson = (url, ms) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ms);

    return fetch(url, { signal: controller.signal })
      .then((response) => response.json())
      .finally(() => clearTimeout(timeoutId));
  };

  const lookup = () =>
    fetchJson('https://get.geojs.io/v1/ip/geo.json', 4000)
      .then((data) => {
        if (!data || !data.country || !data.country_code) throw new Error('geojs: unexpected response');
        return { country: data.country, countryCode: data.country_code };
      })
      .catch(() =>
        fetchJson('https://ipwho.is/', 4000).then((data) => {
          if (!data || data.success === false || !data.country || !data.country_code) return null;
          return { country: data.country, countryCode: data.country_code };
        })
      )
      .catch(() => null);

  let pending;

  /* Resolves to { country, countryCode, name, supported } or null when the country can't be found */
  const get = () => {
    if (!pending) {
      const cached = readCache();

      pending = (cached ? Promise.resolve(cached) : lookup().then((found) => {
        if (found) writeCache(found.country, found.countryCode);
        return found;
      })).then((found) => {
        if (!found) return null;

        const code = found.countryCode.toUpperCase();

        return {
          country: found.country,
          countryCode: code,
          name: config.names[code] || found.country,
          supported: !config.supported || config.supported.includes(code)
        };
      });
    }

    return pending;
  };

  const flagUrl = (countryCode) => `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;

  const flagElement = (countryCode, className = 'geo-flag') => {
    const flag = document.createElement('span');
    flag.className = className;
    flag.innerHTML = `<img src="${flagUrl(countryCode)}" alt="" width="20" loading="lazy">`;
    return flag;
  };

  /* data-geo-shipping-flag: absent = no flag, "" = flag at the start of the line (announcement bar),
     "inline" = flag right after the country name inside the sentence (product page badge) */
  const applyTo = (element, geo) => {
    const template = element.getAttribute('data-geo-shipping-template');

    if (!template || !geo || !geo.supported || element.hasAttribute('data-geo-applied')) return;

    const flagMode = element.getAttribute('data-geo-shipping-flag');
    const [before, after] = template.split('__COUNTRY__');

    element.setAttribute('data-geo-applied', geo.countryCode);

    if (flagMode === 'inline' && after !== undefined) {
      element.textContent = '';
      element.append(before + geo.name, flagElement(geo.countryCode, 'geo-flag geo-flag--after'), after);
      return;
    }

    element.textContent = template.replace('__COUNTRY__', geo.name);

    if (flagMode !== null) element.prepend(flagElement(geo.countryCode));
  };

  const apply = (root) => {
    const targets = root.matches && root.matches(SELECTOR) ? [root] : [];

    if (root.querySelectorAll) targets.push(...root.querySelectorAll(SELECTOR));
    if (!targets.length) return;

    get().then((geo) => targets.forEach((element) => applyTo(element, geo)));
  };

  window.MeloraGeo = { get, apply };

  apply(document);

  /* The product page re-renders its info when a variant is picked, and the theme editor re-renders
     sections: personalise whatever gets inserted later too. */
  new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) apply(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
